import { Injectable, NgModule } from '@angular/core';
import { PreloadingStrategy, Route, RouterModule, Routes, UrlSegment } from '@angular/router';
import { catchError, Observable, of } from 'rxjs';
import { AuthCodeGuard } from './guards/auth-code-guard.guard';

// Every page is loaded on first navigation, so a member landing on e.g. payment-completed doesn't download the activity
// page. See NewVersionNoticeComponent for when such a chunk is gone after a deploy.
/**
 * Preloads lazy routes marked with `data: { preload: true }` in the background once the first page is shown, so the next
 * navigation doesn't wait for the download. A failed preload is ignored here; the real navigation then shows the notice.
 */
@Injectable({ providedIn: 'root' })
export class SelectivePreloadingStrategy implements PreloadingStrategy {
  preload(route: Route, load: () => Observable<unknown>): Observable<unknown> {
    return route.data?.['preload'] ? load().pipe(catchError(() => of(null))) : of(null);
  }
}

const routes: Routes = [
  {
    path: '',
    loadComponent: () => import('./components/login/login.component').then(m => m.LoginComponent),
  },
  {
    path: 'select/organization',
    loadComponent: () =>
      import('./components/select-organization/select-organization.component').then(m => m.SelectOrganizationComponent),
    canActivate: [AuthCodeGuard],
  },
  {
    path: 'join-activity/:data',
    loadComponent: () => import('./components/join-activity/join-activity.component').then(m => m.JoinActivityComponent),
  },
  {
    matcher: url => {
      const guidPattern = /^([0-9A-Fa-f]{8}[-][0-9A-Fa-f]{4}[-][0-9A-Fa-f]{4}[-][0-9A-Fa-f]{4}[-][0-9A-Fa-f]{12})$/gm;
      if (url.length === 3 && url[0].path.match(guidPattern) && url[1].path.match(guidPattern) && url[2].path == 'register') {
        return {
          consumed: url,
          posParams: {
            organizationId: new UrlSegment(url[0].path, {}),
            activityId: new UrlSegment(url[1].path, {}),
          },
        };
      }

      return null;
    },
    loadComponent: () => import('./components/register/register.component').then(m => m.RegisterComponent),
  },
  {
    path: 'payment-completed',
    loadComponent: () =>
      import('./components/payment-completed/payment-completed.component').then(m => m.PaymentCompletedComponent),
  },
  {
    matcher: url => {
      const guidPattern = /^([0-9A-Fa-f]{8}[-][0-9A-Fa-f]{4}[-][0-9A-Fa-f]{4}[-][0-9A-Fa-f]{4}[-][0-9A-Fa-f]{12})$/gm;
      if (url.length === 2 && url[0].path.match(guidPattern) && url[1].path.match(guidPattern)) {
        return {
          consumed: url,
          posParams: {
            organizationId: new UrlSegment(url[0].path, {}),
            activityId: new UrlSegment(url[1].path, {}),
          },
        };
      }

      return null;
    },
    // Login, organization selection and join all lead here, so its chunk is fetched in the background
    data: { preload: true },
    loadComponent: () => import('./components/activity/activity.component').then(m => m.ActivityComponent),
  },
  {
    path: 'not-found',
    loadComponent: () => import('./components/generic-notfound/generic-notfound.component').then(m => m.GenericNotfoundComponent),
  },
  // {
  //   path: ':paymentCode',
  //   component: PaymentComponent,
  // },
  {
    matcher: url => {
      // 5 tecken, A–Z och 0–9 (stora bokstäver)
      const paymentCodePattern = /^[A-Z0-9]{5}$/;

      // Matcha EN enda segment i URL:en
      if (url.length === 1 && paymentCodePattern.test(url[0].path)) {
        return {
          consumed: url,
          posParams: {
            paymentCode: new UrlSegment(url[0].path, {}),
          },
        };
      }

      return null;
    },
    loadComponent: () => import('./components/payment/payment.component').then(m => m.PaymentComponent),
  },
  // { path: '**', redirectTo: 'not-found' },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { preloadingStrategy: SelectivePreloadingStrategy })],
  exports: [RouterModule],
})
export class AppRoutingModule {}
